import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import type { UUID } from '@/types/Id'
import type { NodeType, Permission } from '@/types/Permission'

import { usePermissionLoader } from '@/services/permission-loader/context'
import type { PermissionKey } from '@/services/permission-loader/types'

interface PermissionCheck {
  resourceType: NodeType
  resourceId: UUID
  permission: Permission
}

interface PermissionHookState<TData> {
  data: TData | undefined
  isError: boolean
  isLoading: boolean
  isPending: boolean
  isSuccess: boolean
}

interface PermissionHookResult<TData> extends PermissionHookState<TData> {
  queryEnabled: boolean
}

interface PermissionRequestState<TData> extends PermissionHookState<TData> {
  errorAccessToken?: string | null
  resolvedChecks: PermissionCheck[] | null
}

const INITIAL_STATE: PermissionRequestState<never> = {
  data: undefined,
  isError: false,
  isLoading: false,
  isPending: false,
  isSuccess: false,
  resolvedChecks: null,
}

const getDisabledState = <TData>(): PermissionHookResult<TData> => ({
  ...INITIAL_STATE,
  queryEnabled: false,
})

const getLoadingState = <TData>(
  previous: PermissionRequestState<TData>,
  checks: PermissionCheck[],
): PermissionRequestState<TData> => {
  // The last answer for these same checks stands while they are re-resolved.
  // Re-resolving is not rare: anything the app writes comes back with a fresher
  // ZedToken, which replaces the loader and empties its cache, so a consumer
  // reading `data` alone would see "no permission" for the length of a round
  // trip after every write. The editor turned itself read-only for ~100ms each
  // time somebody sent a chat message that way.
  const isSameChecks = previous.resolvedChecks === checks

  return {
    data: isSameChecks ? previous.data : undefined,
    isError: false,
    isLoading: true,
    isPending: true,
    isSuccess: false,
    resolvedChecks: isSameChecks ? checks : null,
  }
}

const hasErrorResult = (result: boolean | Error): result is Error =>
  result instanceof Error

const arePrimitiveArraysEqual = <T extends string>(
  left: readonly T[],
  right: readonly T[],
) => {
  if (left === right) {
    return true
  }

  if (left.length !== right.length) {
    return false
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false
    }
  }

  return true
}

const areResourceEntriesEqual = (
  left: readonly Pick<PermissionCheck, 'resourceId' | 'resourceType'>[],
  right: readonly Pick<PermissionCheck, 'resourceId' | 'resourceType'>[],
) => {
  if (left === right) {
    return true
  }

  if (left.length !== right.length) {
    return false
  }

  for (let index = 0; index < left.length; index += 1) {
    const leftResource = left[index]
    const rightResource = right[index]

    if (
      leftResource?.resourceId !== rightResource?.resourceId ||
      leftResource?.resourceType !== rightResource?.resourceType
    ) {
      return false
    }
  }

  return true
}

const arePermissionChecksEqual = (
  left: PermissionKey[],
  right: PermissionKey[],
) =>
  areResourceEntriesEqual(left, right) &&
  left.every((check, index) => check.permission === right[index].permission)

const allPermissionsGranted = (
  _checks: PermissionKey[],
  results: readonly (boolean | Error)[],
) => results.every((result) => result === true)

const useStableValue = <T>(
  value: T,
  isEqual: (left: T, right: T) => boolean,
) => {
  const ref = useRef(value)

  if (!isEqual(ref.current, value)) {
    ref.current = value
  }

  return ref.current
}

const buildSinglePermissionMap = (
  checks: PermissionCheck[],
  results: readonly (boolean | Error)[],
) => {
  const data = new Map<Permission, boolean>()

  checks.forEach((check, index) => {
    const result = results[index]
    data.set(
      check.permission,
      hasErrorResult(result) ? false : (result ?? false),
    )
  })

  return data
}

const buildPermissionMap = (
  checks: PermissionCheck[],
  results: readonly (boolean | Error)[],
) => {
  const data = new Map<UUID, Map<Permission, boolean>>()

  checks.forEach((check, index) => {
    const result = results[index]

    if (!data.has(check.resourceId)) {
      data.set(check.resourceId, new Map<Permission, boolean>())
    }

    data
      .get(check.resourceId)!
      .set(check.permission, hasErrorResult(result) ? false : (result ?? false))
  })

  return data
}

const usePermissionChecksState = <TRawData, TSelect = TRawData>({
  checks,
  select,
  transform,
}: {
  checks: PermissionCheck[]
  select?: (data: TRawData) => TSelect
  transform: (
    checks: PermissionCheck[],
    results: readonly (boolean | Error)[],
  ) => TRawData
}): PermissionHookResult<TSelect> => {
  const { accessToken } = useAuthInfo()
  const permissionLoader = usePermissionLoader()
  const [state, setState] =
    useState<PermissionRequestState<TRawData>>(INITIAL_STATE)
  const stateRef = useRef(state)

  stateRef.current = state

  const queryEnabled = !!permissionLoader && checks.length > 0
  const resolvedState = useMemo<PermissionRequestState<TRawData> | null>(() => {
    if (!queryEnabled) {
      return null
    }

    const resolvedResults = permissionLoader.getResolvedValues(checks)
    if (resolvedResults === null) {
      return null
    }

    const rawData = transform(checks, resolvedResults)
    const isError = resolvedResults.some(hasErrorResult)

    return {
      data: rawData,
      isError,
      isLoading: false,
      isPending: false,
      isSuccess: !isError,
      resolvedChecks: checks,
    }
  }, [checks, permissionLoader, queryEnabled, transform])

  useEffect(() => {
    if (!queryEnabled) {
      setState(INITIAL_STATE)
      return
    }

    const currentState = stateRef.current
    const hasCurrentError =
      currentState.isError &&
      currentState.resolvedChecks === checks &&
      queryEnabled
    const shouldRetryCurrentError =
      hasCurrentError && currentState.errorAccessToken !== accessToken

    if (resolvedState && !shouldRetryCurrentError) {
      // Kept as this instance's own answer: one the loader has always answered
      // out of its cache would otherwise leave `getLoadingState` nothing to
      // stand on when the loader is replaced.
      if (stateRef.current.resolvedChecks !== checks) {
        setState(resolvedState)
      }
      return
    }

    if (hasCurrentError && !shouldRetryCurrentError) {
      return
    }

    let isActive = true
    const activeChecks = checks

    setState((previous) => getLoadingState(previous, checks))

    const loadChecks = async () => {
      try {
        const results = await permissionLoader.loadMany(activeChecks)
        if (!isActive) {
          return
        }

        const rawData = transform(activeChecks, results)
        const isError = results.some(hasErrorResult)

        setState({
          data: rawData,
          isError,
          isLoading: false,
          isPending: false,
          isSuccess: !isError,
          errorAccessToken: isError ? accessToken : undefined,
          resolvedChecks: activeChecks,
        })
      } catch {
        if (!isActive) {
          return
        }

        setState({
          data: undefined,
          isError: true,
          isLoading: false,
          isPending: false,
          isSuccess: false,
          errorAccessToken: accessToken,
          resolvedChecks: activeChecks,
        })
      }
    }

    void loadChecks()

    return () => {
      isActive = false
    }
  }, [
    accessToken,
    checks,
    permissionLoader,
    queryEnabled,
    resolvedState,
    transform,
  ])

  const currentState = resolvedState ?? state
  const isResultCurrent = currentState.resolvedChecks === checks

  const data = useMemo(() => {
    if (currentState.data === undefined || !isResultCurrent) {
      return undefined
    }

    return select
      ? select(currentState.data)
      : (currentState.data as unknown as TSelect)
  }, [currentState.data, isResultCurrent, select])

  if (!queryEnabled) {
    return getDisabledState()
  }

  return {
    data,
    isError: isResultCurrent && currentState.isError,
    isLoading: !isResultCurrent || currentState.isLoading,
    isPending: !isResultCurrent || currentState.isPending,
    isSuccess: isResultCurrent && currentState.isSuccess,
    queryEnabled,
  }
}

export interface UsePermissionOptions<TSelect = Map<Permission, boolean>> {
  resourceType: NodeType
  resourceId: UUID
  permissions: Permission[]
  enabled?: boolean
  select?: (data: Map<Permission, boolean>) => TSelect
}

export const useCanReadChatSession = (sessionId: UUID | null | undefined) => {
  const permissionLoader = usePermissionLoader()

  return useCallback(async () => {
    if (!permissionLoader || !sessionId) {
      return null
    }

    const permissionKey: PermissionCheck = {
      resourceType: 'agent_session',
      resourceId: sessionId,
      permission: 'read',
    }

    try {
      permissionLoader.clear(permissionKey)
      return await permissionLoader.load(permissionKey)
    } catch (error) {
      console.error('Failed to verify chat session read permission', error)
      return null
    }
  }, [permissionLoader, sessionId])
}

export const usePermissionCheck = <TSelect = Map<Permission, boolean>>({
  resourceType,
  resourceId,
  permissions,
  enabled = true,
  select,
}: UsePermissionOptions<TSelect>) => {
  const stablePermissions = useStableValue(permissions, arePrimitiveArraysEqual)

  const checks = useMemo<PermissionCheck[]>(() => {
    if (!enabled || !resourceId) {
      return []
    }

    return stablePermissions.map((permission) => ({
      resourceType,
      resourceId,
      permission,
    }))
  }, [enabled, resourceId, resourceType, stablePermissions])

  return usePermissionChecksState({
    checks,
    select,
    transform: buildSinglePermissionMap,
  })
}

export const useAllPermissionsCheck = ({
  checks,
  enabled = true,
}: {
  checks: PermissionKey[]
  enabled?: boolean
}) => {
  const stableChecks = useStableValue(checks, arePermissionChecksEqual)
  const activeChecks = useMemo(
    () => (enabled ? stableChecks : []),
    [enabled, stableChecks],
  )
  return usePermissionChecksState({
    checks: activeChecks,
    transform: allPermissionsGranted,
  })
}

export interface UseBulkPermissionOptions<
  TSelect = Map<UUID, Map<Permission, boolean>>,
> {
  resourceType: NodeType
  resourceIds: UUID[]
  permissions: Permission[]
  enabled?: boolean
  select?: (data: Map<UUID, Map<Permission, boolean>>) => TSelect
}

export const useBulkPermissionCheck = <
  TSelect = Map<UUID, Map<Permission, boolean>>,
>({
  resourceType,
  resourceIds,
  permissions,
  enabled = true,
  select,
}: UseBulkPermissionOptions<TSelect>) => {
  const stablePermissions = useStableValue(permissions, arePrimitiveArraysEqual)
  const stableResourceIds = useStableValue(resourceIds, arePrimitiveArraysEqual)

  const checks = useMemo<PermissionCheck[]>(() => {
    if (!enabled) {
      return []
    }

    return stableResourceIds.flatMap((resourceId) =>
      stablePermissions.map((permission) => ({
        resourceType,
        resourceId,
        permission,
      })),
    )
  }, [enabled, resourceType, stablePermissions, stableResourceIds])

  return usePermissionChecksState({
    checks,
    select,
    transform: buildPermissionMap,
  })
}

export interface UseMixedResourcePermissionCheckOptions<
  TSelect = Map<UUID, Map<Permission, boolean>>,
> {
  resources: Array<{
    resourceType: NodeType
    resourceId: UUID
  }>
  permissions: Permission[]
  enabled?: boolean
  select?: (data: Map<UUID, Map<Permission, boolean>>) => TSelect
}

export const useMixedResourcePermissionCheck = <
  TSelect = Map<UUID, Map<Permission, boolean>>,
>({
  resources,
  permissions,
  enabled = true,
  select,
}: UseMixedResourcePermissionCheckOptions<TSelect>) => {
  const stablePermissions = useStableValue(permissions, arePrimitiveArraysEqual)
  const stableResources = useStableValue(resources, areResourceEntriesEqual)

  const checks = useMemo<PermissionCheck[]>(() => {
    if (!enabled) {
      return []
    }

    return stableResources.flatMap(({ resourceType, resourceId }) =>
      stablePermissions.map((permission) => ({
        resourceType,
        resourceId,
        permission,
      })),
    )
  }, [enabled, stablePermissions, stableResources])

  return usePermissionChecksState({
    checks,
    select,
    transform: buildPermissionMap,
  })
}
